<script setup lang="ts">
import Clock from '@primeicons/vue/clock';
import ExclamationTriangle from '@primeicons/vue/exclamation-triangle';
import Card from 'primevue/card';
import Tag from 'primevue/tag';
import Button from 'primevue/button';
import { formatDistanceToNow, parseISO } from 'date-fns';
import { computed, ref } from 'vue';
import { useStore } from 'vuex';
import type { ShipmentExceptionRow } from '@gpx-spa/queries/src/shipment-exception-types';
import usePostAccountExceptionClosed from '@gpx-spa/queries/src/mutations/postAccountExceptionClosed';
import useDeleteAccountExceptionClosed from '@gpx-spa/queries/src/mutations/deleteAccountExceptionClosed';
import {
  exceptionTypeDescription,
  exceptionTypeTitle,
} from '@/utils/shipment-exception-catalog';
import {
  exceptionAccentBarClass,
  exceptionIconDisplayForSeverity,
  exceptionSeverityLabel,
} from '@/utils/shipment-exception-display';
import { formatTime } from '@/filters';
import ExceptionCloseFeedbackDialog from '@/components/shipment/exceptions/ExceptionCloseFeedbackDialog.vue';
import NamedTrackerSet from '@/components/shipment/shared/NamedTrackerSet.vue';
import type { NamedTracker } from '@/utils/named-tracker-set';
import { namedTrackersFromIds } from '@/utils/named-tracker-set';

const props = defineProps<{
  row: ShipmentExceptionRow;
  devices?: NamedTracker[];
}>();

const store = useStore();
const accountId = computed(() => store.getters['auth/accountId'] as number);
const closeMutation = usePostAccountExceptionClosed();
const reopenMutation = useDeleteAccountExceptionClosed();
const feedbackOpen = ref(false);

const cardPt = {
  root: {
    class:
      '!rounded-none overflow-hidden border border-neutral-300 bg-white shadow-sm !p-0',
  },
  body: { class: '!p-0' },
  content: { class: '!p-0' },
};

function tagSeverity(
  severity: string,
):
  | 'success'
  | 'info'
  | 'warn'
  | 'danger'
  | 'secondary'
  | 'contrast'
  | undefined {
  const s = severity.toLowerCase();
  if (s === 'critical' || s === 'high') return 'danger';
  if (s === 'warning' || s === 'medium') return 'warn';
  if (s === 'info' || s === 'low') return 'info';
  return 'secondary';
}

function exceptionCardIconClass(severity: string): string {
  return (
    exceptionIconDisplayForSeverity(severity)?.colorClass ?? 'text-neutral-600'
  );
}

function occurredDate(row: ShipmentExceptionRow): Date | null {
  if (!row.occurred_at) return null;
  try {
    return parseISO(row.occurred_at);
  } catch {
    return null;
  }
}

function formatRelative(row: ShipmentExceptionRow): string {
  const d = occurredDate(row);
  return d ? formatDistanceToNow(d, { addSuffix: true }) : '—';
}

function formatAbsolute(row: ShipmentExceptionRow): string {
  const d = occurredDate(row);
  return d ? (formatTime(d, 'MMM d, h:mm aa') ?? '') : '';
}

const exceptionTrackers = computed(() =>
  namedTrackersFromIds(props.row.device_ids ?? [], props.devices ?? []),
);

async function onConfirmClose(payload: {
  feedback: 'real_issue' | 'not_an_issue' | null;
  note: string;
}) {
  await closeMutation.mutateAsync({
    accountId: accountId.value,
    exceptionId: props.row.id,
    shipmentId: props.row.shipment_id,
    feedback: payload.feedback,
    note: payload.note || null,
  });
  feedbackOpen.value = false;
}

async function onReopen() {
  await reopenMutation.mutateAsync({
    accountId: accountId.value,
    exceptionId: props.row.id,
    shipmentId: props.row.shipment_id,
  });
}

const isBusy = computed(
  () => closeMutation.isPending.value || reopenMutation.isPending.value,
);
</script>

<template>
  <Card :pt="cardPt">
    <template #content>
      <div class="relative flex min-h-px">
        <div
          class="w-1 shrink-0 self-stretch"
          :class="exceptionAccentBarClass(row.severity)"
          aria-hidden="true"
        />
        <div class="flex min-w-0 flex-1 flex-col gap-1 px-3 py-2">
          <div class="flex items-start justify-between gap-2">
            <div class="flex min-w-0 items-start gap-1.5">
              <ExclamationTriangle
                :class="[
                  'mt-px shrink-0 leading-none',
                  exceptionCardIconClass(row.severity),
                ]"
                :size="14"
                aria-hidden="true"
              />
              <span
                class="text-[13px] font-semibold leading-tight text-neutral-900"
              >
                {{ exceptionTypeTitle(row.type) }}
              </span>
            </div>
            <div class="flex shrink-0 items-center gap-1.5">
              <Tag
                :value="exceptionSeverityLabel(row.severity)"
                :severity="tagSeverity(row.severity)"
                class="!text-[10px] !font-semibold uppercase tracking-wide"
                :pt="{ root: { class: '!px-1.5 !py-0.5' } }"
              />
              <Tag
                v-if="row.status === 'closed'"
                value="Closed"
                severity="secondary"
                class="!text-[10px] !font-semibold uppercase tracking-wide"
                :pt="{ root: { class: '!px-1.5 !py-0.5' } }"
              />
            </div>
          </div>

          <p class="text-[12px] leading-snug text-gray-500">
            {{ exceptionTypeDescription(row.type) }}
          </p>

          <div
            v-if="row.status === 'closed' && row.feedback"
            class="flex flex-wrap items-center gap-2"
          >
            <Tag
              :value="
                row.feedback === 'real_issue' ? 'Real issue' : 'Not an issue'
              "
              :severity="row.feedback === 'real_issue' ? 'danger' : 'success'"
              class="!text-[10px] !font-semibold tracking-wide"
              :pt="{ root: { class: '!px-1.5 !py-0.5' } }"
            />
            <span
              v-if="row.feedback_note"
              class="min-w-0 text-[12px] italic leading-snug text-neutral-500"
            >
              “{{ row.feedback_note }}”
            </span>
          </div>

          <div
            class="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 border-t border-neutral-200 pt-1 text-[10px] text-neutral-600"
          >
            <NamedTrackerSet :trackers="exceptionTrackers" />
            <span class="text-neutral-400">·</span>
            <span class="inline-flex items-center gap-0.5">
              <Clock class="text-neutral-500" :size="9" aria-hidden="true" />
              <time class="tabular-nums text-neutral-700">{{
                formatRelative(row)
              }}</time>
            </span>
            <span v-if="formatAbsolute(row)" class="text-neutral-500"
              >({{ formatAbsolute(row) }})</span
            >
            <span class="ml-auto flex gap-1">
              <Button
                v-if="row.status === 'open'"
                label="Close"
                size="small"
                text
                class="!px-1 !py-0 !text-[11px]"
                :disabled="isBusy"
                @click="feedbackOpen = true"
              />
              <Button
                v-else
                label="Reopen"
                size="small"
                text
                class="!px-1 !py-0 !text-[11px]"
                :disabled="isBusy"
                @click="onReopen"
              />
            </span>
          </div>
        </div>
      </div>
    </template>
  </Card>

  <ExceptionCloseFeedbackDialog
    v-model:visible="feedbackOpen"
    :is-submitting="closeMutation.isPending.value"
    @confirm="onConfirmClose"
  />
</template>
